/* eslint-disable */
import { Editor } from '@ckeditor/ckeditor5-core';
import { AxiosError, AxiosResponse } from 'axios';
import { DefaultTFuncReturn } from 'i18next';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ErrorInfo, OriSearchResult } from '../../../../../api/model';
import { usePostSectionCopy } from '../../../../../api/sections/sections';
import { useDefaultOptions } from '../../../../../hooks/api';
import * as sessionStorageUtils from '../../../../../utils/sessionStorage';
import ErrorComponent from '../../../../atoms/ErrorComponent';
import NoDataComponent from '../../../../atoms/NoDataComponent';
import WaitComponent from '../../../../atoms/WaitComponent';
import ConfirmModal from '../../../../modules/ConfirmModal';
import ErrorModal from '../../../../modules/ErrorModal';
import PageNavi from '../../../../modules/PageNavi';
import SpinnerModal from '../../../../modules/SpinnerModal';

// 文字列を 12 文字目まで表示する
const shorten = (text: string | undefined) => ((text?.length ?? 0) > 12 ? `${text?.substring(0, 12) ?? ''}...` : text);

// 制作一覧のソートキー
type SortKey =
  | 'production_id'
  | 'sales_my'
  | 'model_name'
  | 'production_no'
  | 'title'
  | 'destination_name'
  | 'plan_from_on'
  | 'maker_company_name'
  | 'chapter_name'
  | 'level'
  | 'plain_text';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

/**
 * ソート可能なテーブルヘッダー
 * @param param0
 * @returns
 */
function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

// 1ページに表示する件数
const N_DATA_PER_PAGE = 20;

interface Props {
  editor: Editor;
  productionId: number | undefined;
  data: AxiosResponse<OriSearchResult, unknown> | undefined;
  isLoading: boolean;
  isError: boolean;
  error: AxiosError<ErrorInfo> | null;
  setSelectSectionsRef: Dispatch<SetStateAction<number[]>>;
}

/**
 * 制作一覧
 * @param param0
 * @returns
 */
function OriList({ editor, productionId, data, isLoading, isError, error, setSelectSectionsRef }: Props) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // #region 表示ロジック
  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(1);
  const [datalength, setDataLength] = useState(0);

  // ソート情報
  const [sortInfo, setSortInfo] = useState({ key: '' as SortKey, isDesc: false });

  // チェックボックスの状態
  const [selectSectionIds, setSelectSections] = useState<number[]>([]);
  useEffect(
    // useEffectを用いないと、Warning: Cannot update a component (`OriPage`) while rendering a different component (`OriList`).というワーニングが表示される
    () => setSelectSectionsRef(selectSectionIds), // 親に渡す
    [selectSectionIds, setSelectSectionsRef]
  );

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 一覧データをソートしたもの
  const sortedOri = useMemo(
    () =>
      [...(data?.data.ori ?? [])].sort((a, b) => {
        const aAny = a[sortInfo.key] ?? '';
        const bAny = b[sortInfo.key] ?? '';
        const direction = sortInfo.isDesc ? -1 : 1;

        if (aAny < bAny) {
          return -1 * direction;
        }
        if (aAny > bAny) {
          return 1 * direction;
        }
        return 0;
      }),
    [sortInfo, data]
  );

  // 現在のページに表示する一覧
  const OriForPresentPage = useMemo(
    () => sortedOri.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, sortedOri]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: SortKey) => {
    setSortInfo((pre) => {
      const next = { key, isDesc: pre.key === key && !pre.isDesc };
      sessionStorageUtils.set('Productions.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // データが変更されたときの動作
  useEffect(() => {
    if (data?.data.ori != null) {
      // 一覧読込の際はチェックをクリア
      setSelectSections([]);

      setDataLength(data?.data.ori.length);
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [data?.data.ori]);

  /* 全チェックON/OFF */
  const handleAllSelectSection = useCallback(() => {
    if (data?.data.ori === undefined) {
      setSelectSections([]);
    } else if (selectSectionIds.length === data?.data.ori.length) {
      setSelectSections([]);
    } else {
      const sectionIds: number[] = [];
      data?.data.ori.forEach((ori) => {
        if (ori.section_id) sectionIds.push(ori.section_id);
      });
      setSelectSections(sectionIds);
    }
  }, [setSelectSections, selectSectionIds, data]);

  const handleSelectUser = useCallback(
    (SectionId: number) => {
      if (selectSectionIds.includes(SectionId)) {
        setSelectSections(selectSectionIds.filter((id) => id !== SectionId));
      } else {
        setSelectSections([...selectSectionIds, SectionId]);
      }
    },
    [setSelectSections, selectSectionIds]
  );

  // 行メニューの開閉処理
  const [sectionIdForOpenedMenu, setSectionIdForOpenedMenu] = useState('');
  const onOpenMenuClicked = useCallback(
    (sectionId: string) =>
      sectionId === sectionIdForOpenedMenu ? setSectionIdForOpenedMenu('') : setSectionIdForOpenedMenu(sectionId),
    [sectionIdForOpenedMenu]
  );
  // #endregion

  // #region コピー
  // コピー処理定義
  const options = useDefaultOptions(language);
  const {
    mutateAsync: copyAsync,
    error: copyError,
    isLoading: copyIsLoading,
    isError: copyIsError,
  } = usePostSectionCopy<AxiosError<ErrorInfo>>(options);
  const [displayCopyError, setDisplayCopyError] = useState(false);
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);
  const [selectSectionId, setSelectSectionId] = useState<number>(0);

  useEffect(() => {
    setDisplayCopyError(copyIsError);
  }, [copyIsError]);

  // コピー
  const onCopyCliked = useCallback(
    async (sectionId: number) => {
      setDisplaySpinnerModal(true);
      let xml = '';
      await copyAsync(
        {
          sectionId,
          data: { production_id: productionId },
        },
        {
          onSuccess: (response) => {
            xml = String(response.data.content_xml);
          },
        }
      );
      setSectionIdForOpenedMenu('');
      if (xml !== '') {
        await global.navigator.clipboard.writeText(xml);
      }
    },
    [copyAsync, productionId]
  );
  // #endregion

  // #region ORI挿入
  const [confirmPsInsert, setConfirmPsInsert] = useState(false); // 確認ダイアログ

  /** 挿入 */
  const insertOri = useCallback(
    async (sectionId: number) => {
      // console.log(`section_id:${sectionId}`);

      // カーソルがsection内にあるかチェック
      const searchPsDiv: any = function (element: any) {
        if (
          element.name === 'l5Section' ||
          element.name === 'l4Section' ||
          element.name === 'l3Section' ||
          element.name === 'l2Section'
        ) {
          return element;
        }
        if (!element.parent) return undefined;
        return searchPsDiv(element.parent);
      };

      await copyAsync(
        {
          sectionId,
          data: { production_id: productionId },
        },
        {
          onSuccess: (response) => {
            if (response.status === 200) {
              if (response.data.content_xml !== undefined) {
                const pasteXml = response.data.content_xml;
                editor.model.change((writer) => {
                  // 現在のカーソル
                  const { selection } = editor.model.document;

                  // 現在選択中のElementを取得
                  const elements = Array.from(selection.getSelectedBlocks());
                  if (elements[0]) {
                    const element = elements[0];

                    // 自身がPSの要素か確認し、PSの要素ならDIV要素を返す
                    const sectionElement = searchPsDiv(element);
                    if (sectionElement) {
                      console.log(sectionElement);
                      // カーソルを該当PSDIVの後ろに持っていく
                      writer.setSelection(sectionElement, 'after');
                      // HTMLを入れ込む
                      const viewFragment = editor.data.processor.toView(pasteXml);
                      const modelFragment = editor.data.toModel(viewFragment);
                      editor.model.insertContent(modelFragment);
                    }
                  }
                });
              }
            }
          },
        }
      );
      setSectionIdForOpenedMenu('');
    },
    [productionId, editor, setSectionIdForOpenedMenu]
  );
  // #endregion

  // データ取得中
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return <ErrorComponent error={error} />;
  }

  // データが空の場合
  if (refIsFirstLoading.current === false) {
    if (data == null || data.data.ori == null) {
      return <NoDataComponent />;
    }
  }

  return (
    <div className="wfr in-pgnavi documentlist">
      <div className="scroll" style={{ height: '350px' }}>
        <table className="table16 in-elloutput2 sticky_table">
          <thead>
            {/* ヘッダー部 */}
            <tr>
              <th>
                {' '}
                <span className="ck-box">
                  <input
                    type="checkbox"
                    id="ck59-1"
                    checked={datalength === selectSectionIds.length}
                    onChange={() => handleAllSelectSection()}
                  />{' '}
                  <label htmlFor="ck59-1">&nbsp;</label>
                </span>
              </th>
              <SortableTh label={t('M/Y')} onClick={() => changeSortKey('sales_my')} />
              <SortableTh label={t('機種名')} onClick={() => changeSortKey('model_name')} />
              <SortableTh label={t('資料管理No')} onClick={() => changeSortKey('production_no')} />
              <SortableTh label={t('タイトル')} onClick={() => changeSortKey('title')} />
              <SortableTh label={t('仕向')} onClick={() => changeSortKey('destination_name')} />
              <SortableTh label={t('完成日')} onClick={() => changeSortKey('plan_from_on')} />
              <SortableTh label={t('原作メーカー')} onClick={() => changeSortKey('maker_company_name')} />
              <SortableTh label={t('章')} onClick={() => changeSortKey('chapter_name')} />
              <SortableTh label={t('レベル')} onClick={() => changeSortKey('level')} />
              <SortableTh label={t('文章')} onClick={() => changeSortKey('plain_text')} />
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {/* データ本体 */}
            {OriForPresentPage.map((x) => (
              <tr key={x.section_id}>
                <td>
                  <span className="ck-box">
                    <input
                      type="checkbox"
                      id={String(x.section_id)}
                      checked={x.section_id ? selectSectionIds.includes(x.section_id) : false}
                      onChange={() => x.section_id && handleSelectUser(x.section_id)}
                    />
                    <label htmlFor={String(x.section_id)}>&nbsp;</label>
                  </span>
                </td>
                <td>{shorten(x.sales_my)}</td>
                <td>{shorten(x.model_name)}</td>
                <td>{shorten(x.production_no)}</td>
                <td>{shorten(x.title)}</td>
                <td>{shorten(x.destination_name)}</td>
                <td>{shorten(x.plan_from_on)}</td>
                <td>{shorten(x.maker_company_name)}</td>
                <td>{shorten(x.chapter_name)}</td>
                <td>{shorten(x.level)}</td>
                <td>
                  <Link
                    to={`/${language}/productions/sections/${x.production_id?.toString() ?? ''}/ps?section_id=${Number(
                      x.l2_section_id
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {' '}
                    {shorten(x.plain_text)}
                  </Link>
                </td>
                <td className="elloutput">
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => onOpenMenuClicked(x.section_id?.toString() ?? '')}
                  >
                    <i className="fa-solid fa-ellipsis-vertical" />
                  </button>
                  <div
                    className={sectionIdForOpenedMenu === x.section_id?.toString() ? 'open' : ''}
                    style={{ left: '-70px' }}
                  >
                    <ul>
                      <li className="modal-open">
                        <button
                          type="button"
                          className="text-button"
                          onClick={() => onCopyCliked(Number(x.section_id))}
                        >
                          <span>
                            <i className="fa-solid fa-copy" />
                            {t('コピー')}
                          </span>
                        </button>
                      </li>
                      <li className="modal-open">
                        <button
                          type="button"
                          className="text-button"
                          onClick={() => {
                            setSelectSectionId(Number(x.section_id));
                            setConfirmPsInsert(true);
                          }}
                        >
                          <span>
                            <i className="fa-solid fa-plus-square" />
                            {t('挿入')}
                          </span>
                        </button>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* ページング部分 */}
      <PageNavi
        current={currentPage}
        pageCount={Math.ceil(Number(data?.data?.ori?.length) / (N_DATA_PER_PAGE * 1.0))}
        onChangePage={changeCurrentPage}
      />
      {copyIsLoading && <SpinnerModal />}
      {/* ダウンロードエラー */}
      {displayCopyError && <ErrorModal error={copyError} onClickOk={() => setDisplayCopyError(false)} />}
      {/* ORI挿入 */}
      {confirmPsInsert && (
        <ConfirmModal
          onClickCancel={() => setConfirmPsInsert(false)}
          onClickOk={async () => {
            setConfirmPsInsert(false);
            void insertOri(selectSectionId);
            // void getPS(true);
          }}
        >
          {t('該当セクションの文書を挿入しますか？')}
        </ConfirmModal>
      )}
    </div>
  );
}

export default OriList;
